import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getWmoInfo } from '../../utils/wmoWeather';
import type { CityWeather } from '../../services/lifeCenterService';
import type { Attraction } from '../../services/attractionService';
import type { HealthRecord } from '../../services/lifeDataService';
import type { MyLocation } from '../../services/eeMyLocationsService';

export interface BoundsInfo {
  north: number; south: number; east: number; west: number; zoom: number;
}

interface Props {
  weatherData: CityWeather[];
  showWeather: boolean;
  lang: string;
  onCityClick: (city: CityWeather, clientX: number, clientY: number) => void;
  onLocationClick?: (lat: number, lng: number, clientX: number, clientY: number) => void;
  onContextMenu?: (lat: number, lng: number, clientX: number, clientY: number) => void;
  // Tourism
  showTourism: boolean;
  attractionData: Attraction[];
  onAttractionClick: (attr: Attraction, clientX: number, clientY: number) => void;
  onBoundsChange: (bounds: BoundsInfo) => void;
  // Trip overlay
  tripAttractions: Attraction[];
  onTripAttractionClick: (attr: Attraction, clientX: number, clientY: number) => void;
  // Health overlay
  showHealth?: boolean;
  healthMarkers?: HealthRecord[];
  onHealthLocationClick?: (lat: number, lng: number, clientX: number, clientY: number) => void;
  onHealthMarkerClick?: (record: HealthRecord, clientX: number, clientY: number) => void;
  // Fly to a geocoded location (Nominatim search query)
  flyToQuery?: string;
  // Marking mode: click on globe to place a pending pin
  markingMode?: boolean;
  myLocations?: MyLocation[];
  onPendingMarkerContextMenu?: (lat: number, lng: number, x: number, y: number) => void;
  onMyLocationClick?: (loc: MyLocation, x: number, y: number) => void;
  // Fullscreen
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

// ── Map styles ────────────────────────────────────────────────────────────────
type StyleKey = 'dark' | 'light' | 'satellite' | 'cyclosm' | 'standard'
  | 'tgos_emap' | 'tgos_photo' | 'tgos_cadastral';   // 台灣 TGOS / 內政部國土測繪中心 WMTS

const STYLES: Record<StyleKey, string | object> = {
  dark:  'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  light: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
  // satellite style is built dynamically in buildSatelliteHybridStyle()
  // to support locale-aware vector labels; this entry is the fallback only
  satellite: {
    version: 8,
    sources: {
      sat: {
        type: 'raster',
        tiles: [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution: '© Esri, Maxar, Earthstar Geographics',
      },
    },
    layers: [{ id: 'sat', type: 'raster', source: 'sat' }],
  },
  cyclosm: {
    version: 8,
    sources: {
      cyclosm: {
        type: 'raster',
        tiles: [
          'https://a.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
          'https://b.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
          'https://c.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
        ],
        tileSize: 256,
        maxzoom: 17,
        attribution: '© <a href="https://www.cyclosm.org">CyclOSM</a> contributors, © OpenStreetMap contributors',
      },
    },
    layers: [{ id: 'cyclosm', type: 'raster', source: 'cyclosm' }],
  },
  standard: {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  },
  // ── 台灣 TGOS · 內政部國土測繪中心 WMTS（注意 tile 路徑為 {z}/{y}/{x}）──
  tgos_emap: {
    version: 8,
    sources: {
      tgos: {
        type: 'raster',
        tiles: ['https://wmts.nlsc.gov.tw/wmts/EMAP5/default/GoogleMapsCompatible/{z}/{y}/{x}'],
        tileSize: 256, maxzoom: 19,
        attribution: '© 內政部國土測繪中心',
      },
    },
    layers: [{ id: 'tgos', type: 'raster', source: 'tgos' }],
  },
  tgos_photo: {
    version: 8,
    sources: {
      tgos_photo: {
        type: 'raster',
        tiles: ['https://wmts.nlsc.gov.tw/wmts/PHOTO2/default/GoogleMapsCompatible/{z}/{y}/{x}'],
        tileSize: 256, maxzoom: 19,
        attribution: '© 內政部國土測繪中心 · 正射影像',
      },
    },
    layers: [{ id: 'tgos_photo', type: 'raster', source: 'tgos_photo' }],
  },
  tgos_cadastral: {
    version: 8,
    sources: {
      tgos_emap: {
        type: 'raster',
        tiles: ['https://wmts.nlsc.gov.tw/wmts/EMAP5/default/GoogleMapsCompatible/{z}/{y}/{x}'],
        tileSize: 256, maxzoom: 19,
        attribution: '© 內政部國土測繪中心',
      },
      tgos_landsect: {
        type: 'raster',
        tiles: ['https://wmts.nlsc.gov.tw/wmts/LANDSECT/default/GoogleMapsCompatible/{z}/{y}/{x}'],
        tileSize: 256, maxzoom: 19,
        attribution: '© 內政部國土測繪中心 · 地籍',
      },
    },
    layers: [
      { id: 'tgos_emap', type: 'raster', source: 'tgos_emap' },
      { id: 'tgos_landsect', type: 'raster', source: 'tgos_landsect' },   // 地籍界疊在電子地圖上
    ],
  },
};

const STYLE_LABELS: Record<StyleKey, { zh: string; en: string; icon: string }> = {
  dark:      { icon: '🌑', zh: '深色',   en: 'Dark'      },
  light:     { icon: '☀️', zh: '明亮',   en: 'Light'     },
  satellite: { icon: '🛰️', zh: '衛星',   en: 'Satellite' },
  cyclosm:   { icon: '🚲', zh: '單車',   en: 'Cycle'     },
  standard:  { icon: '🗺️', zh: 'OSM',    en: 'OSM'       },
  tgos_emap:      { icon: '🗺️', zh: '台灣街道', en: 'Taiwan Street' },
  tgos_photo:     { icon: '🛰️', zh: '高解衛星', en: 'Taiwan Photo'  },
  tgos_cadastral: { icon: '🏠', zh: '地籍圖',   en: 'Cadastral'     },
};

// globe→Mercator transition happens around zoom 5
const GLOBE_MAX_ZOOM = 5;

// ── Map label localisation ────────────────────────────────────────────────────
// CARTO / OpenMapTiles tiles carry name_zh and name_en fields.
// After every style load we rewrite all symbol layers to prefer the right locale.
function applyMapLocale(map: maplibregl.Map, lang: string) {
  const isZh = !lang.startsWith('en');
  const style = map.getStyle();
  if (!style?.layers) return;
  style.layers.forEach(layer => {
    if (layer.type !== 'symbol') return;
    if (!(layer as any).layout?.['text-field']) return;
    try {
      map.setLayoutProperty(
        layer.id,
        'text-field',
        isZh
          ? ['coalesce', ['get', 'name_zh'], ['get', 'name']]
          : ['coalesce', ['get', 'name_en'], ['get', 'name']],
      );
    } catch { /* raster / satellite layers have no text — ignore */ }
  });
}

// ── Globe-side visibility helper ──────────────────────────────────────────────
// Returns true if the geographic point (lat, lng) is on the visible hemisphere
// centred at (cLat, cLng).  dot > threshold keeps a small buffer at the rim.
function isOnFrontHemisphere(
  lat: number, lng: number,
  cLat: number, cLng: number,
  threshold = 0.05,
): boolean {
  const D = Math.PI / 180;
  const mx = Math.cos(lat * D) * Math.cos(lng * D);
  const my = Math.cos(lat * D) * Math.sin(lng * D);
  const mz = Math.sin(lat * D);
  const cx = Math.cos(cLat * D) * Math.cos(cLng * D);
  const cy = Math.cos(cLat * D) * Math.sin(cLng * D);
  const cz = Math.sin(cLat * D);
  return (mx * cx + my * cy + mz * cz) > threshold;
}

// ── Satellite hybrid style builder ───────────────────────────────────────────
// Fetches CARTO voyager style, keeps only symbol (label) layers, and combines
// with the ESRI satellite raster so applyMapLocale can localise labels normally.
async function buildSatelliteHybridStyle(): Promise<any> {
  const res  = await fetch('https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json');
  const base = await res.json();
  const labelLayers = (base.layers as any[]).filter((l: any) => l.type === 'symbol');
  return {
    version: 8,
    glyphs:  base.glyphs,
    sprite:  base.sprite,
    sources: {
      ...base.sources,
      sat: {
        type: 'raster',
        tiles: [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        maxzoom:  19,
        attribution: '© Esri, Maxar, Earthstar Geographics',
      },
    },
    layers: [
      { id: 'sat', type: 'raster', source: 'sat' },
      ...labelLayers,
    ],
  };
}

// ── User location helpers ─────────────────────────────────────────────────────
function createCircleGeoJSON(lat: number, lng: number, radiusM: number): GeoJSON.FeatureCollection {
  const steps = 64;
  const coords: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    coords.push([
      lng + (radiusM / 6371000) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180) * Math.sin(a),
      lat + (radiusM / 6371000) * (180 / Math.PI) * Math.cos(a),
    ]);
  }
  return { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] }, properties: {} }] };
}

function addUserLocCircle(map: maplibregl.Map, lat: number, lng: number, accuracy: number) {
  const data = createCircleGeoJSON(lat, lng, accuracy);
  if (map.getSource('user-loc-circle')) {
    (map.getSource('user-loc-circle') as maplibregl.GeoJSONSource).setData(data as any);
  } else {
    map.addSource('user-loc-circle', { type: 'geojson', data: data as any });
    map.addLayer({ id: 'user-loc-circle-fill', type: 'fill', source: 'user-loc-circle', paint: { 'fill-color': '#4285f4', 'fill-opacity': 0.14 } });
    map.addLayer({ id: 'user-loc-circle-line', type: 'line', source: 'user-loc-circle', paint: { 'line-color': '#4285f4', 'line-opacity': 0.55, 'line-width': 1.5 } });
  }
}

function removeUserLocCircle(map: maplibregl.Map) {
  try { map.removeLayer('user-loc-circle-fill'); } catch { /* already gone */ }
  try { map.removeLayer('user-loc-circle-line'); } catch { /* already gone */ }
  try { map.removeSource('user-loc-circle'); } catch { /* already gone */ }
}

// ── Bbox overlay helpers ──────────────────────────────────────────────────────
function bboxToGeoJSON(b: maplibregl.LngLatBounds): GeoJSON.Feature {
  const w = b.getWest(), e = b.getEast(), n = b.getNorth(), s = b.getSouth();
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
    },
    properties: {},
  };
}

function updateBboxOverlay(map: maplibregl.Map) {
  const src = map.getSource('bbox-bounds') as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  src.setData(bboxToGeoJSON(map.getBounds()));
}

function initBboxOverlay(map: maplibregl.Map) {
  map.addSource('bbox-bounds', {
    type: 'geojson',
    data: bboxToGeoJSON(map.getBounds()),
  });
  map.addLayer({
    id:     'bbox-line',
    type:   'line',
    source: 'bbox-bounds',
    layout: { visibility: 'none' },
    paint:  {
      'line-color':    '#ff8c42',
      'line-width':    1.5,
      'line-dasharray': [4, 4],
      'line-opacity':  0.7,
    },
  });
}

// ── Component ─────────────────────────────────────────────────────────────────
type MarkerEntry = { marker: maplibregl.Marker; lat: number; lng: number };

const GlobeView: React.FC<Props> = ({
  weatherData, showWeather, lang,
  onCityClick, onLocationClick, onContextMenu,
  showTourism, attractionData, onAttractionClick, onBoundsChange,
  tripAttractions, onTripAttractionClick,
  showHealth = false, healthMarkers = [],
  onHealthLocationClick, onHealthMarkerClick,
  flyToQuery,
  markingMode = false, myLocations = [],
  onPendingMarkerContextMenu, onMyLocationClick,
  isFullscreen = false, onToggleFullscreen,
}) => {
  const containerRef          = useRef<HTMLDivElement>(null);
  const mapRef                = useRef<maplibregl.Map | null>(null);
  const markersRef            = useRef<MarkerEntry[]>([]);
  const attractionMarkersRef  = useRef<MarkerEntry[]>([]);
  const tripMarkersRef        = useRef<MarkerEntry[]>([]);
  const healthMarkersRef      = useRef<MarkerEntry[]>([]);
  const myLocMarkersRef       = useRef<(MarkerEntry & { id: number })[]>([]);
  const pendingMarkerRef      = useRef<maplibregl.Marker | null>(null);
  const rafRef                = useRef<number>(0);

  const [mapLoaded,    setMapLoaded]    = useState(false);
  const [currentStyle, setCurrentStyle] = useState<StyleKey>('standard');
  const [styleVersion, setStyleVersion] = useState(0);
  const skipFirstStyleEffect            = useRef(true);

  const [userLocState, setUserLocState] = useState<'idle' | 'locating' | 'located'>('idle');
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const [searchText, setSearchText]       = useState('');
  const [searching, setSearching]         = useState(false);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const userLocMarkerRef   = useRef<maplibregl.Marker | null>(null);
  const userLocCircleRef   = useRef<{ lat: number; lng: number; accuracy: number } | null>(null);
  const searchMarkerRef    = useRef<maplibregl.Marker | null>(null);

  // Ref to the visibility-update function — keeps it out of effect deps
  const syncVisibilityRef = useRef<() => void>(() => {});

  const onCityClickRef            = useRef(onCityClick);
  const onLocationClickRef        = useRef(onLocationClick);
  const onContextMenuRef          = useRef(onContextMenu);
  const onAttractionClickRef      = useRef(onAttractionClick);
  const onBoundsChangeRef         = useRef(onBoundsChange);
  const onTripAttractionClickRef  = useRef(onTripAttractionClick);
  const onHealthLocationClickRef       = useRef(onHealthLocationClick);
  const onHealthMarkerClickRef         = useRef(onHealthMarkerClick);
  const showHealthRef                  = useRef(showHealth);
  const showTourismRef                 = useRef(showTourism);
  const langRef                        = useRef(lang);
  const markingModeRef                 = useRef(markingMode);
  const onPendingMarkerContextMenuRef  = useRef(onPendingMarkerContextMenu);
  const onMyLocationClickRef           = useRef(onMyLocationClick);
  useEffect(() => { onCityClickRef.current           = onCityClick; },           [onCityClick]);
  useEffect(() => { onLocationClickRef.current       = onLocationClick; },       [onLocationClick]);
  useEffect(() => { onContextMenuRef.current         = onContextMenu; },         [onContextMenu]);
  useEffect(() => { onAttractionClickRef.current     = onAttractionClick; },     [onAttractionClick]);
  useEffect(() => { onBoundsChangeRef.current        = onBoundsChange; },        [onBoundsChange]);
  useEffect(() => { onTripAttractionClickRef.current = onTripAttractionClick; }, [onTripAttractionClick]);
  useEffect(() => { onHealthLocationClickRef.current = onHealthLocationClick; }, [onHealthLocationClick]);
  useEffect(() => { onHealthMarkerClickRef.current   = onHealthMarkerClick; },   [onHealthMarkerClick]);
  useEffect(() => { showHealthRef.current            = showHealth; },            [showHealth]);
  useEffect(() => { showTourismRef.current                  = showTourism; },                  [showTourism]);
  useEffect(() => { langRef.current                        = lang; },                          [lang]);
  useEffect(() => { markingModeRef.current                 = markingMode; },                   [markingMode]);
  useEffect(() => { onPendingMarkerContextMenuRef.current  = onPendingMarkerContextMenu; },    [onPendingMarkerContextMenu]);
  useEffect(() => { onMyLocationClickRef.current           = onMyLocationClick; },             [onMyLocationClick]);

  // ── initialise map (once) ──────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container:          containerRef.current,
      style:              STYLES.standard as any,
      center:             [121, 25],
      zoom:               2,
      projection:         { type: 'globe' },
      attributionControl: false,
    } as maplibregl.MapOptions);

    mapRef.current = map;

    // Visibility sync: hide markers on back of globe or outside viewport
    syncVisibilityRef.current = () => {
      const m = mapRef.current;
      if (!m) return;
      const zoom   = m.getZoom();
      const center = m.getCenter();

      [...markersRef.current, ...attractionMarkersRef.current, ...tripMarkersRef.current, ...healthMarkersRef.current, ...myLocMarkersRef.current].forEach(({ marker, lat, lng }) => {
        // Step 1 — hemisphere check (always). Primary guard: map.project() can
        // wrap far-away points back into pixel range in Mercator mode, causing
        // back-hemisphere cities to bleed through. Pure trig is always reliable.
        const onFront = isOnFrontHemisphere(lat, lng, center.lat, center.lng);

        let visible: boolean;
        if (!onFront) {
          visible = false;
        } else if (zoom >= GLOBE_MAX_ZOOM) {
          // Step 2 — geographic bounds check (Mercator mode only).
          // getBounds() returns real lat/lng of the viewport; no pixel-wrapping issues.
          const b   = m.getBounds();
          const PAD = 3; // degrees buffer so labels don't pop at exact edge
          const bS  = b.getSouth() - PAD;
          const bN  = b.getNorth() + PAD;
          const bW  = b.getWest()  - PAD;
          const bE  = b.getEast()  + PAD;
          const inLat = lat >= bS && lat <= bN;
          // Handle antimeridian: west > east when the viewport crosses 180°
          const inLng = bW <= bE
            ? (lng >= bW && lng <= bE)
            : (lng >= bW || lng <= bE);
          visible = inLat && inLng;
        } else {
          // Globe low-zoom: front hemisphere = visible
          visible = true;
        }

        (marker.getElement() as HTMLElement).style.visibility =
          visible ? 'visible' : 'hidden';
      });
    };

    // Update visibility whenever the map moves or zooms
    map.on('move', () => syncVisibilityRef.current());

    // Fire bounds after every pan/zoom — LifeCenterPage debounces and queries attractions
    map.on('moveend', () => {
      const b = map.getBounds();
      onBoundsChangeRef.current({
        north: b.getNorth(), south: b.getSouth(),
        east:  b.getEast(),  west:  b.getWest(),
        zoom:  map.getZoom(),
      });
      // Update bbox overlay
      updateBboxOverlay(map);
    });

    // Re-apply globe projection + locale after every style reload
    map.on('style.load', () => {
      (map as any).setProjection?.({ type: 'globe' });
      applyMapLocale(map, langRef.current);
      try {
        initBboxOverlay(map);
        if (showTourismRef.current) {
          map.setLayoutProperty('bbox-line', 'visibility', 'visible');
        }
      } catch { /* ignore if source already exists */ }
      setStyleVersion(v => v + 1);
    });

    // Navigation control removed — replaced by custom unified panel in render

    map.on('load', () => {
      setMapLoaded(true);

      // On load: centre globe on user's GPS position at zoom 1, or fall back to Taiwan
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            map.flyTo({
              center: [pos.coords.longitude, pos.coords.latitude],
              zoom: 1,
              duration: 1500,
            });
          },
          () => {
            map.flyTo({ center: [121, 25], zoom: 1, duration: 1200 });
          },
          { enableHighAccuracy: true, timeout: 6000 },   // 高精度，避免低精度 IP cache 污染
        );
      } else {
        map.flyTo({ center: [121, 25], zoom: 1, duration: 1200 });
      }
    });

    // Close layer menu when map is clicked
    map.on('click', () => setLayerMenuOpen(false));

    // Click-anywhere → marking mode or weather/health location
    map.on('click', (e: maplibregl.MapMouseEvent) => {
      const oe = e.originalEvent as MouseEvent;
      if (markingModeRef.current) {
        const { lat, lng } = e.lngLat;
        if (pendingMarkerRef.current) { pendingMarkerRef.current.remove(); pendingMarkerRef.current = null; }
        const el = document.createElement('div');
        el.style.cssText = [
          'width:28px;height:28px;cursor:pointer;',
          'background:url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 28\'%3E%3Cpath d=\'M12 0C7.6 0 4 3.6 4 8c0 6 8 20 8 20s8-14 8-20c0-4.4-3.6-8-8-8z\' fill=\'%23ff6f00\'/%3E%3Ccircle cx=\'12\' cy=\'8\' r=\'3\' fill=\'white\'/%3E%3C/svg%3E") no-repeat center/contain;',
        ].join('');
        el.title = '右鍵 → 設為我的地點';
        el.addEventListener('contextmenu', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onPendingMarkerContextMenuRef.current?.(lat, lng, ev.clientX, ev.clientY);
        });
        pendingMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([lng, lat])
          .addTo(map);
        return;
      }
      if (showHealthRef.current) {
        onHealthLocationClickRef.current?.(e.lngLat.lat, e.lngLat.lng, oe.clientX, oe.clientY);
      } else {
        onLocationClickRef.current?.(e.lngLat.lat, e.lngLat.lng, oe.clientX, oe.clientY);
      }
    });

    // Right-click → context menu
    map.on('contextmenu', (e: maplibregl.MapMouseEvent) => {
      const oe = e.originalEvent as MouseEvent;
      oe.preventDefault();
      onContextMenuRef.current?.(e.lngLat.lat, e.lngLat.lng, oe.clientX, oe.clientY);
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ── fly to geocoded location (from calendar or external link) ────────────────
  useEffect(() => {
    if (!flyToQuery) return;
    const map = mapRef.current;
    if (!map) return;
    if (searchMarkerRef.current) { searchMarkerRef.current.remove(); searchMarkerRef.current = null; }
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(flyToQuery)}&format=json&limit=1`)
      .then(r => r.json())
      .then((results: { lat: string; lon: string }[]) => {
        if (!results.length) return;
        const lat = parseFloat(results[0].lat);
        const lon = parseFloat(results[0].lon);
        map.flyTo({ center: [lon, lat], zoom: 11, duration: 1500 });
        // Place a green marker so the user can see where the location is
        const el = document.createElement('div');
        el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:default;user-select:none;';
        const labelText = flyToQuery.length > 22 ? flyToQuery.slice(0, 22) + '…' : flyToQuery;
        el.innerHTML = `
          <div style="
            background:rgba(5,40,15,0.92);border:1.5px solid rgba(76,175,80,0.8);
            border-radius:8px;padding:3px 9px;text-align:center;
            backdrop-filter:blur(6px);white-space:nowrap;
            box-shadow:0 2px 8px rgba(76,175,80,0.25);
          ">
            <div style="color:#81c784;font-size:11px;font-weight:600;">📅 ${labelText}</div>
          </div>
          <div style="width:2px;height:8px;background:rgba(76,175,80,0.7);"></div>
          <div style="width:10px;height:10px;border-radius:50%;background:#4caf50;
            box-shadow:0 0 0 4px rgba(76,175,80,0.28),0 0 10px rgba(76,175,80,0.5);"></div>
        `;
        searchMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([lon, lat])
          .addTo(map);
      })
      .catch(() => {});
  }, [flyToQuery]);

  // ── marking mode cursor ───────────────────────────────────────────────────
  useEffect(() => {
    const canvas = containerRef.current?.querySelector('canvas');
    if (canvas) (canvas as HTMLElement).style.cursor = markingMode ? 'crosshair' : '';
    if (!markingMode && pendingMarkerRef.current) {
      pendingMarkerRef.current.remove();
      pendingMarkerRef.current = null;
    }
    // Disable pointer events on saved location markers so clicks pass through to map canvas
    myLocMarkersRef.current.forEach(({ marker }) => {
      (marker.getElement() as HTMLElement).style.pointerEvents = markingMode ? 'none' : '';
    });
  }, [markingMode]);

  // ── my-locations markers ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    myLocMarkersRef.current.forEach(({ marker }) => marker.remove());
    myLocMarkersRef.current = [];
    myLocations.forEach(loc => {
      // 【漂移根因】maplibre 會在 marker element 上加 .maplibregl-marker { position:absolute }，
      // 並用 translate(x, y) 定位到地理座標對應的像素位置。
      // 若 wrapper 設了 inline style position:relative，inline style 優先級高於 class，
      // position:absolute 被覆蓋 → translate 改為相對於父元素 flow 位置計算，
      // 導致標記出現在距正確座標幾百公尺到數公里外的地方。
      // 解法：wrapper 不設 position，改用 flex column，label 以 margin-bottom 堆疊在 pin 上方。
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'cursor:pointer;display:inline-flex;flex-direction:column;align-items:center;';
      const label = document.createElement('div');
      label.textContent = loc.name;
      label.style.cssText = [
        'background:rgba(21,101,192,.9);color:white;font-size:10px;font-weight:600;',
        'padding:2px 6px;border-radius:3px;white-space:nowrap;pointer-events:none;',
        'box-shadow:0 1px 4px rgba(0,0,0,.4);margin-bottom:2px;',
      ].join('');
      const pin = document.createElement('div');
      pin.style.cssText = [
        'width:26px;height:28px;',
        'background:url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 28\'%3E%3Cpath d=\'M12 0C7.6 0 4 3.6 4 8c0 6 8 20 8 20s8-14 8-20c0-4.4-3.6-8-8-8z\' fill=\'%231565c0\'/%3E%3Ccircle cx=\'12\' cy=\'8\' r=\'3\' fill=\'white\'/%3E%3C/svg%3E") no-repeat center/contain;',
      ].join('');
      pin.title = loc.name;
      wrapper.appendChild(label);
      wrapper.appendChild(pin);
      wrapper.addEventListener('click', (ev) => {
        ev.stopPropagation();
        onMyLocationClickRef.current?.(loc, ev.clientX, ev.clientY);
      });
      const marker = new maplibregl.Marker({ element: wrapper, anchor: 'bottom' })
        .setLngLat([loc.lng, loc.lat])
        .addTo(map);
      myLocMarkersRef.current.push({ marker, id: loc.id, lat: loc.lat, lng: loc.lng });
    });
    syncVisibilityRef.current();
  }, [myLocations, mapLoaded]);

  // ── style switcher ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (skipFirstStyleEffect.current) { skipFirstStyleEffect.current = false; return; }
    const map = mapRef.current;
    if (!map) return;
    if (currentStyle === 'satellite') {
      buildSatelliteHybridStyle()
        .then(style => { if (mapRef.current) mapRef.current.setStyle(style); })
        .catch(() => { if (mapRef.current) mapRef.current.setStyle(STYLES.satellite as any); });
    } else {
      map.setStyle(STYLES[currentStyle] as any);
    }
  }, [currentStyle]);

  // ── re-apply locale when language changes (style already loaded) ───────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    applyMapLocale(map, lang);
  }, [lang, mapLoaded, styleVersion]);

  // ── bbox overlay visibility ────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    try {
      map.setLayoutProperty('bbox-line', 'visibility', showTourism ? 'visible' : 'none');
      if (showTourism) updateBboxOverlay(map);
    } catch { /* layer not ready yet */ }
  }, [showTourism, mapLoaded]);

  // ── restore user-location circle after style reload ───────────────────────
  useEffect(() => {
    if (userLocState !== 'located' || !mapLoaded || !userLocCircleRef.current) return;
    const map = mapRef.current;
    if (!map) return;
    const { lat, lng, accuracy } = userLocCircleRef.current;
    addUserLocCircle(map, lat, lng, accuracy);
  }, [styleVersion, userLocState, mapLoaded]);

  // ── user location toggle ───────────────────────────────────────────────────
  const handleToggleUserLocation = () => {
    if (userLocState === 'located') {
      const map = mapRef.current;
      if (map) removeUserLocCircle(map);
      if (userLocMarkerRef.current) { userLocMarkerRef.current.remove(); userLocMarkerRef.current = null; }
      userLocCircleRef.current = null;
      setUserLocState('idle');
      return;
    }
    if (userLocState === 'locating') return;
    if (!navigator.geolocation) return;
    setUserLocState('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        const map = mapRef.current;
        if (!map) { setUserLocState('idle'); return; }
        userLocCircleRef.current = { lat, lng, accuracy };
        addUserLocCircle(map, lat, lng, accuracy);
        if (!userLocMarkerRef.current) {
          const el = document.createElement('div');
          el.style.cssText = 'width:14px;height:14px;border-radius:50%;background:#4285f4;border:2.5px solid #fff;box-shadow:0 0 6px rgba(66,133,244,0.9),0 0 0 5px rgba(66,133,244,0.22);pointer-events:none;';
          userLocMarkerRef.current = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
        } else {
          userLocMarkerRef.current.setLngLat([lng, lat]);
        }
        map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 13), duration: 1200 });
        setUserLocState('located');
      },
      () => setUserLocState('idle'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },   // 不吃 IP-based cache，強制重抓真實 GPS
    );
  };

  // ── map text search (Nominatim forward geocoding) ────────────────────────────
  const handleSearch = async () => {
    if (!searchText.trim()) return;
    const map = mapRef.current;
    if (!map) return;
    setSearching(true);
    setSearchNotFound(false);
    // Remove previous search result marker
    if (searchMarkerRef.current) { searchMarkerRef.current.remove(); searchMarkerRef.current = null; }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchText.trim())}&format=json&limit=1`,
        { headers: { 'User-Agent': 'ap-base-ee/1.0', 'Accept-Language': lang.startsWith('en') ? 'en' : 'zh-TW,en' } },
      );
      const results: { lat: string; lon: string; display_name?: string }[] = await res.json();
      if (results.length > 0) {
        const lat = parseFloat(results[0].lat);
        const lon = parseFloat(results[0].lon);
        map.flyTo({ center: [lon, lat], zoom: 14, duration: 1500 });
        // Place a green result marker at the found location
        const el = document.createElement('div');
        el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:default;user-select:none;';
        const label = searchText.trim().length > 22 ? searchText.trim().slice(0, 22) + '…' : searchText.trim();
        el.innerHTML = `
          <div style="
            background:rgba(5,40,15,0.92);border:1.5px solid rgba(76,175,80,0.8);
            border-radius:8px;padding:3px 9px;text-align:center;
            backdrop-filter:blur(6px);white-space:nowrap;
            box-shadow:0 2px 8px rgba(76,175,80,0.25);
          ">
            <div style="color:#81c784;font-size:11px;font-weight:600;">📍 ${label}</div>
          </div>
          <div style="width:2px;height:8px;background:rgba(76,175,80,0.7);"></div>
          <div style="width:10px;height:10px;border-radius:50%;background:#4caf50;
            box-shadow:0 0 0 4px rgba(76,175,80,0.28),0 0 10px rgba(76,175,80,0.5);"></div>
        `;
        searchMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([lon, lat])
          .addTo(map);
      } else {
        setSearchNotFound(true);
      }
    } catch { /* network error — silently ignore */ }
    finally { setSearching(false); }
  };

  // ── fire initial bounds when tourism is toggled on ─────────────────────────
  useEffect(() => {
    if (!showTourism || !mapLoaded) return;
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    onBoundsChangeRef.current({
      north: b.getNorth(), south: b.getSouth(),
      east:  b.getEast(),  west:  b.getWest(),
      zoom:  map.getZoom(),
    });
  }, [showTourism, mapLoaded]);

  // ── attraction markers ─────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    attractionMarkersRef.current.forEach(({ marker }) => marker.remove());
    attractionMarkersRef.current = [];

    if (!showTourism || attractionData.length === 0) return;

    attractionData.forEach(attr => {
      const name = lang === 'en'
        ? (attr.nameEN || attr.nameZH)
        : (attr.nameZH || attr.nameEN);
      if (!name) return;

      const el = document.createElement('div');
      el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;';
      el.innerHTML = `
        <div style="
          background:rgba(40,18,5,0.88);
          border:1px solid rgba(255,140,66,0.65);
          border-radius:8px;padding:3px 8px;
          text-align:center;backdrop-filter:blur(6px);white-space:nowrap;
        ">
          <div style="color:#fff;font-size:11px;font-weight:600;">🏛️ ${name}</div>
        </div>
        <div style="width:2px;height:8px;background:rgba(255,140,66,0.6);"></div>
        <div style="width:6px;height:6px;border-radius:50%;background:#ff8c42;"></div>
      `;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onAttractionClickRef.current(attr, e.clientX, e.clientY);
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([attr.lng, attr.lat])
        .addTo(map);

      attractionMarkersRef.current.push({ marker, lat: attr.lat, lng: attr.lng });
    });

    syncVisibilityRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, showTourism, attractionData, lang, styleVersion]);

  // ── trip overlay markers (purple, numbered) ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    tripMarkersRef.current.forEach(({ marker }) => marker.remove());
    tripMarkersRef.current = [];

    if (tripAttractions.length === 0) return;

    tripAttractions.forEach((attr, idx) => {
      const name = lang === 'en'
        ? (attr.nameEN || attr.nameZH)
        : (attr.nameZH || attr.nameEN);
      if (!name) return;

      const num = idx + 1;
      const el = document.createElement('div');
      el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;';
      el.innerHTML = `
        <div style="
          background:rgba(30,10,50,0.90);
          border:1px solid rgba(155,89,182,0.75);
          border-radius:8px;padding:3px 8px;
          text-align:center;backdrop-filter:blur(6px);white-space:nowrap;
          display:flex;align-items:center;gap:5px;
        ">
          <span style="
            background:#9b59b6;color:#fff;
            font-size:10px;font-weight:700;
            border-radius:50%;width:16px;height:16px;
            display:flex;align-items:center;justify-content:center;flex-shrink:0;
          ">${num}</span>
          <span style="color:#fff;font-size:11px;font-weight:600;">${name}</span>
        </div>
        <div style="width:2px;height:8px;background:rgba(155,89,182,0.6);"></div>
        <div style="width:6px;height:6px;border-radius:50%;background:#9b59b6;"></div>
      `;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onTripAttractionClickRef.current(attr, e.clientX, e.clientY);
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([attr.lng, attr.lat])
        .addTo(map);

      tripMarkersRef.current.push({ marker, lat: attr.lat, lng: attr.lng });
    });

    syncVisibilityRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, tripAttractions, lang, styleVersion]);

  // ── health markers (🩺, colour-coded by value) ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    healthMarkersRef.current.forEach(({ marker }) => marker.remove());
    healthMarkersRef.current = [];

    if (!showHealth || healthMarkers.length === 0) return;

    healthMarkers.forEach(record => {
      if (!record.location) return;
      const { lat, lng } = record.location;

      // Color coding
      let color = '#42a5f5';
      if (record.type === '血糖' && record.values[0] != null) {
        const v = record.values[0];
        color = v > 200 ? '#ef5350' : v >= 126 ? '#ffa726' : '#66bb6a';
      } else if (record.type === '血壓' && record.values[0] != null) {
        color = record.values[0] > 140 ? '#ef5350' : '#66bb6a';
      }

      const time = record.ts.slice(11, 16);
      const valLabel = record.values.length > 0
        ? (record.type === '血壓' && record.values.length >= 3
            ? `${record.values[0]}/${record.values[1]}`
            : String(record.values[0]))
        : '';

      const el = document.createElement('div');
      el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;';
      el.innerHTML = `
        <div style="
          background:rgba(20,5,15,0.88);
          border:1px solid ${color}aa;
          border-radius:8px;padding:3px 8px;
          text-align:center;backdrop-filter:blur(6px);white-space:nowrap;
        ">
          <span style="color:#fff;font-size:11px;font-weight:600;">🩺 ${time}${valLabel ? ' ' + valLabel : ''}</span>
        </div>
        <div style="width:2px;height:8px;background:${color}99;"></div>
        <div style="width:6px;height:6px;border-radius:50%;background:${color};"></div>
      `;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onHealthMarkerClickRef.current?.(record, e.clientX, e.clientY);
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);

      healthMarkersRef.current.push({ marker, lat, lng });
    });

    syncVisibilityRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, showHealth, healthMarkers, styleVersion]);

  // ── city markers ───────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = [];

    if (!showWeather || weatherData.length === 0) return;

    // Text colours adapt to map style
    const textColor   = currentStyle === 'light' ? '#1a1a2e'              : '#fff';
    const tempColor   = currentStyle === 'light' ? '#1565c0'              : '#7ec8ff';
    const bgColor     = currentStyle === 'light' ? 'rgba(255,255,255,0.90)' : 'rgba(15,25,50,0.80)';
    const borderColor = currentStyle === 'light' ? 'rgba(66,133,244,0.4)'  : 'rgba(66,133,244,0.5)';

    weatherData.forEach(city => {
      const cityName = lang === 'en' ? (city.nameEn || city.name) : city.name;
      const { label } = getWmoInfo(city.weatherCode, lang);

      const el = document.createElement('div');
      el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;';
      el.innerHTML = `
        <div style="
          background:${bgColor};border:1px solid ${borderColor};
          border-radius:8px;padding:4px 8px;text-align:center;
          backdrop-filter:blur(6px);white-space:nowrap;
        ">
          <div style="font-size:14px;">${city.emoji}</div>
          <div style="color:${textColor};font-size:11px;font-weight:600;">${cityName}</div>
          <div style="color:${tempColor};font-size:12px;font-weight:700;">${city.temp}°C</div>
        </div>
        <div style="width:2px;height:8px;background:rgba(66,133,244,0.6);"></div>
        <div style="width:6px;height:6px;border-radius:50%;background:#4285f4;"></div>
      `;
      el.title = label;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onCityClickRef.current(city, e.clientX, e.clientY);
      });
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenuRef.current?.(city.lat, city.lng, e.clientX, e.clientY);
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([city.lng, city.lat])
        .addTo(map);

      markersRef.current.push({ marker, lat: city.lat, lng: city.lng });
    });

    // Immediately apply back-face culling after markers are rebuilt
    syncVisibilityRef.current();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, showWeather, weatherData, lang, currentStyle, styleVersion]);

  // ── render ─────────────────────────────────────────────────────────────────
  const styleKeys: StyleKey[] = ['dark', 'light', 'satellite', 'cyclosm', 'standard',
    'tgos_emap', 'tgos_photo', 'tgos_cadastral'];

  return (
    <>
      <style>{`
        .maplibregl-ctrl-group {
          background: rgba(15,25,50,0.82) !important;
          border: 1px solid rgba(66,133,244,0.35) !important;
          border-radius: 8px !important;
          overflow: hidden;
          backdrop-filter: blur(6px);
        }
        .maplibregl-ctrl-group button {
          background: transparent !important;
          border-bottom: 1px solid rgba(66,133,244,0.18) !important;
        }
        .maplibregl-ctrl-group button:last-child { border-bottom: none !important; }
        .maplibregl-ctrl-group button:hover { background: rgba(66,133,244,0.22) !important; }
        .maplibregl-ctrl-icon { filter: invert(1) brightness(0.85) !important; }
        .maplibregl-ctrl-attrib { display: none; }
      `}</style>

      <div ref={containerRef} style={{
        width: '100%', height: '100%',
        filter: currentStyle === 'light' ? 'brightness(0.82) contrast(0.95)' : undefined,
      }} />

      {/* ── Search bar — top centre ── */}
      <div style={{
        position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
        zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          background: 'rgba(15,25,50,0.85)', backdropFilter: 'blur(8px)',
          border: `1px solid ${searchNotFound ? 'rgba(255,100,100,0.6)' : 'rgba(66,133,244,0.4)'}`,
          borderRadius: 8, overflow: 'hidden', transition: 'border-color 0.2s',
        }}>
          <input
            value={searchText}
            onChange={e => { setSearchText(e.target.value); setSearchNotFound(false); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={lang.startsWith('en') ? 'Search places…' : '搜尋地點、地址…'}
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: '#fff', fontSize: 13, padding: '8px 12px',
              width: 260, minWidth: 0,
            }}
          />
          {searchText && (
            <button
              onClick={() => { setSearchText(''); setSearchNotFound(false); if (searchMarkerRef.current) { searchMarkerRef.current.remove(); searchMarkerRef.current = null; } }}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '0 8px', color: 'rgba(255,255,255,0.38)', fontSize: 15, lineHeight: 1,
              }}
            >✕</button>
          )}
          <button
            onClick={handleSearch}
            disabled={searching || !searchText.trim()}
            style={{
              background: 'rgba(66,133,244,0.18)',
              border: 'none', borderLeft: '1px solid rgba(66,133,244,0.28)',
              cursor: searching || !searchText.trim() ? 'default' : 'pointer',
              padding: '8px 12px', display: 'flex', alignItems: 'center',
              opacity: !searchText.trim() ? 0.5 : 1, transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (searchText.trim() && !searching) e.currentTarget.style.background = 'rgba(66,133,244,0.32)'; }}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(66,133,244,0.18)')}
          >
            {searching ? (
              <svg width="15" height="15" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(66,133,244,0.4)" strokeWidth="2.5"/>
                <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="#4285f4" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="rgba(255,255,255,0.75)">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            )}
          </button>
        </div>
        {searchNotFound && (
          <div style={{
            marginTop: 5,
            background: 'rgba(15,25,50,0.9)', border: '1px solid rgba(255,100,100,0.4)',
            borderRadius: 6, padding: '4px 12px',
            color: 'rgba(255,160,160,0.9)', fontSize: 11, whiteSpace: 'nowrap',
          }}>
            {lang.startsWith('en') ? 'No results found' : '找不到符合的地點'}
          </div>
        )}
      </div>

      {/* ── Unified top-right control panel: Fullscreen → Zoom → Location → Layer ── */}
      <div style={{
        position: 'absolute', top: 10, right: 10, zIndex: 10,
        display: 'flex', flexDirection: 'column',
        background: 'rgba(15,25,50,0.82)',
        border: '1px solid rgba(66,133,244,0.35)',
        borderRadius: 8, overflow: 'visible',
        backdropFilter: 'blur(6px)',
      }}>
        {/* Fullscreen */}
        {onToggleFullscreen && (
          <Tooltip title={isFullscreen ? '離開全螢幕' : '全螢幕'} placement="left" arrow>
            <button onClick={onToggleFullscreen}
              style={{
                width: 44, height: 44, background: 'transparent', border: 'none',
                borderBottom: '1px solid rgba(66,133,244,0.35)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px 8px 0 0', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(66,133,244,0.22)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {isFullscreen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="rgba(255,255,255,0.82)">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="rgba(255,255,255,0.82)">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              )}
            </button>
          </Tooltip>
        )}

        {/* Zoom in */}
        <Tooltip title={lang.startsWith('en') ? 'Zoom in' : '放大'} placement="left" arrow>
          <button onClick={() => mapRef.current?.zoomIn()}
            style={{
              width: 44, height: 44, background: 'transparent', border: 'none',
              borderBottom: '1px solid rgba(66,133,244,0.18)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.82)', fontSize: 28, lineHeight: 1,
              borderRadius: 0, transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(66,133,244,0.22)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >+</button>
        </Tooltip>

        {/* Zoom out */}
        <Tooltip title={lang.startsWith('en') ? 'Zoom out' : '縮小'} placement="left" arrow>
          <button onClick={() => mapRef.current?.zoomOut()}
            style={{
              width: 44, height: 44, background: 'transparent', border: 'none',
              borderBottom: '1px solid rgba(66,133,244,0.18)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.82)', fontSize: 28, lineHeight: 1,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(66,133,244,0.22)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >−</button>
        </Tooltip>

        {/* Reset north (compass) */}
        <Tooltip title={lang.startsWith('en') ? 'Reset north' : '重設方向'} placement="left" arrow>
          <button onClick={() => (mapRef.current as any)?.resetNorthPitch?.()}
            style={{
              width: 44, height: 44, background: 'transparent', border: 'none',
              borderBottom: '1px solid rgba(66,133,244,0.35)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(66,133,244,0.22)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <svg width="28" height="28" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="7.5" stroke="rgba(66,133,244,0.55)" strokeWidth="1"/>
              <polygon points="9,2.5 7.2,9 9,7.8 10.8,9" fill="#ef5350"/>
              <polygon points="9,15.5 7.2,9 9,10.2 10.8,9" fill="rgba(255,255,255,0.55)"/>
            </svg>
          </button>
        </Tooltip>

        {/* Location */}
        <Tooltip title={userLocState === 'located' ? (lang.startsWith('en') ? 'Cancel location' : '取消定位') : (lang.startsWith('en') ? 'My location' : '我的位置')} placement="left" arrow>
          <button
            onClick={handleToggleUserLocation}
            style={{
              width: 44, height: 44,
              background: userLocState === 'located' ? 'rgba(66,133,244,0.22)' : 'transparent',
              border: 'none', borderBottom: '1px solid rgba(66,133,244,0.18)',
              cursor: userLocState === 'locating' ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (userLocState !== 'located') e.currentTarget.style.background = 'rgba(66,133,244,0.22)'; }}
            onMouseLeave={e => { if (userLocState !== 'located') e.currentTarget.style.background = 'transparent'; }}
          >
          {userLocState === 'locating' ? (
            <svg width="22" height="22" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
              <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
              <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(66,133,244,0.4)" strokeWidth="2.5"/>
              <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="#4285f4" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill={userLocState === 'located' ? '#4285f4' : 'rgba(255,255,255,0.8)'}>
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
            </svg>
          )}
          </button>
        </Tooltip>

        {/* Layer switcher button */}
        <div style={{ position: 'relative' }}>
          {layerMenuOpen && (
            <div style={{
              position: 'absolute', top: 0, right: 'calc(100% + 6px)',
              background: 'rgba(15,25,50,0.94)', border: '1px solid rgba(66,133,244,0.35)',
              borderRadius: 8, overflow: 'hidden', backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.45)', minWidth: 108,
            }}>
              {styleKeys.map((key, i) => {
                const { icon, zh, en } = STYLE_LABELS[key];
                const label = lang === 'en' ? en : zh;
                const active = currentStyle === key;
                return (
                  <button key={key}
                    onClick={() => { setCurrentStyle(key); setLayerMenuOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7,
                      width: '100%', padding: '7px 12px', cursor: 'pointer',
                      background: active ? 'rgba(66,133,244,0.28)' : 'transparent',
                      color: active ? '#7ec8ff' : 'rgba(255,255,255,0.75)',
                      fontSize: 12, fontWeight: active ? 700 : 400, border: 'none',
                      borderBottom: i < styleKeys.length - 1 ? '1px solid rgba(66,133,244,0.15)' : 'none',
                    }}>
                    <span style={{ fontSize: 14 }}>{icon}</span>{label}
                  </button>
                );
              })}
            </div>
          )}
          <Tooltip title={lang.startsWith('en') ? 'Map layers' : '地圖圖層'} placement="left" arrow>
            <button onClick={() => setLayerMenuOpen(o => !o)}
              style={{
                width: 44, height: 44,
                background: layerMenuOpen ? 'rgba(66,133,244,0.25)' : 'transparent',
                border: 'none', borderRadius: '0 0 8px 8px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (!layerMenuOpen) e.currentTarget.style.background = 'rgba(66,133,244,0.22)'; }}
              onMouseLeave={e => { if (!layerMenuOpen) e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill={layerMenuOpen ? '#7ec8ff' : 'rgba(255,255,255,0.8)'}>
                <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
              </svg>
            </button>
          </Tooltip>
        </div>
      </div>

    </>
  );
};

export default GlobeView;
